import * as React from 'react'
import { Disclosure, DisclosurePanel, Button as AriaButton } from 'react-aria-components'
import { CaretDown } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'

/**
 * Panel-level defaults for the {@link DetailSection}s inside it. A container
 * (e.g. `DetailPanel`) provides this so every section adopts the same chrome
 * (like `rule`) without setting it per section; an explicit prop on a section
 * still wins.
 */
export interface DetailSectionDefaults {
  rule?: boolean
  /** Heading level for sections in this container (e.g. a panel sets 2). */
  level?: 1 | 2 | 3 | 4 | 5 | 6
}
export const DetailSectionDefaultsContext =
  React.createContext<DetailSectionDefaults>({})

/**
 * DetailSection — a titled, collapsible section: the organizing unit of a
 * detail-page sidebar (Summary, Related Companies, Contacts, Tags). The section
 * chrome; its body is composed from other primitives — an `InfoList` (key-value
 * summary), a `Tree` (an expandable record list — chain → doors), a stack of
 * `EntitySummaryCard`s (a featured related entity), or a chip cluster (tags).
 *
 *   <DetailSection title="Related Companies" count={3}>
 *     …tree / cards / rows…
 *     <AddPill>Add company</AddPill>
 *   </DetailSection>
 *
 * The header is an accordion row: a quiet uppercase **eyebrow** on the left (so
 * the record names in the body stay focal — the CRM-sidebar norm) and the
 * expand/collapse **caret on the right**. The whole row is the toggle. Add and
 * other affordances live in the **body**, below the items — not in the header —
 * so every section in a shared panel reads the same. `titleVariant` `strong`
 * gives a louder title for a section that should dominate; off by default,
 * `rule` draws a hairline from the title to the caret.
 *
 * Built on React Aria's `Disclosure` (like {@link Collapsible}) for real
 * disclosure semantics. `headerRight` is an optional section-level action that
 * overlays just left of the caret (a hover-pencil edit); `revealActions` hides
 * it until the section is hovered or focus lands inside it. `collapsible`
 * `false` drops the caret and keeps the body always open.
 */

export interface DetailSectionProps {
  /** Section title. */
  title: React.ReactNode
  /**
   * Title treatment. `eyebrow` (default) — a quiet uppercase gray label that
   * keeps the body content focal (the sidebar norm). `strong` — a heavier
   * gray-800 title for a section meant to dominate (a main-content section).
   */
  titleVariant?: 'eyebrow' | 'strong'
  /** Draw a hairline rule from the title to the right edge. Default `false`. */
  rule?: boolean
  /** Optional muted count after the title (e.g. `3` related companies). */
  count?: number
  /** Right-aligned header actions — an edit pencil, an Add pill, a toggle. */
  headerRight?: React.ReactNode
  /** Reveal `headerRight` only on hover / focus-within (the hover-pencil). */
  revealActions?: boolean
  /** Collapsible with a caret (default). `false` = always-open, no caret. */
  collapsible?: boolean
  /** Initial open state — uncontrolled. Default `true`. */
  defaultOpen?: boolean
  /** Open state — controlled. Pair with `onOpenChange`. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /**
   * Heading level around the title. Defaults to the container's level (a
   * `DetailPanel` sets 2), else 3. Set explicitly to override.
   */
  level?: 1 | 2 | 3 | 4 | 5 | 6
  className?: string
  children?: React.ReactNode
}

export const DetailSection = ({
  title,
  titleVariant = 'eyebrow',
  rule,
  count,
  headerRight,
  revealActions,
  collapsible = true,
  defaultOpen = true,
  open,
  onOpenChange,
  level,
  className,
  children,
}: DetailSectionProps) => {
  const defaults = React.useContext(DetailSectionDefaultsContext)
  // An explicit `rule` wins; otherwise inherit the panel-level default.
  const effectiveRule = rule ?? defaults.rule
  // Explicit `level` wins; else inherit the container default (a panel sets 2);
  // else 3 (a section is typically nested under a page h1/h2).
  const effectiveLevel = level ?? defaults.level ?? 3
  const Heading = `h${effectiveLevel}` as 'h3'
  const titleText = (
    <span className="flex min-w-0 items-center gap-2">
      <span
        className={cn(
          'truncate',
          titleVariant === 'eyebrow'
            ? 'text-xs font-semibold uppercase tracking-wide text-fbui-gray-500'
            : 'text-sm font-semibold text-fbui-gray-800'
        )}
      >
        {title}
      </span>
      {count != null && (
        // A soft brand-green count chip — a calm indicator, not a loud number.
        <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-fbui-basil-50 px-1.5 text-xs font-semibold tabular-nums text-fbui-basil-700">
          {count}
        </span>
      )}
    </span>
  )
  // A flex spacer that pushes the caret to the right edge; it becomes a visible
  // hairline when `rule` is set (off by default — the clean eyebrow look).
  const spacer = (
    <span
      className={cn('flex-1', effectiveRule && 'h-px bg-fbui-gray-200')}
      aria-hidden="true"
    />
  )
  // Section-level action (a hover-pencil edit) — overlays just left of the caret
  // so it never nests inside the toggle button; `revealActions` fades it in on
  // hover / keyboard focus. With a `rule`, the action carries the surface color
  // so it cleanly interrupts the hairline instead of sitting on top of it (the
  // mask fades away with the action, so a hidden reveal-action leaves the rule
  // whole).
  const actions = headerRight != null && (
    <div
      className={cn(
        'absolute inset-y-0 flex items-center',
        collapsible ? 'right-6' : 'right-0',
        effectiveRule && 'bg-fbui-base-white px-1',
        // Arbitrary `[opacity:*]` (not `opacity-0/100`) so the reveal survives
        // the consuming app's Bootstrap, whose `.opacity-0 { opacity:0
        // !important }` would otherwise pin the action invisible on hover.
        // `has-[:focus-visible]` (not `focus-within`): when the action opens a
        // dialog and the user closes it with the mouse, focus returns to the
        // trigger but *not* as focus-visible, so the pencil hides instead of
        // staying stuck lit until the next click. Keyboard users still see it.
        revealActions &&
          '[opacity:0] transition-opacity has-[:focus-visible]:[opacity:1] group-hover/detail-section:[opacity:1]'
      )}
    >
      {headerRight}
    </div>
  )
  const caret = (
    <CaretDown
      aria-hidden="true"
      weight="bold"
      className="size-3.5 shrink-0 text-fbui-gray-500 transition-transform duration-200 group-data-[expanded]/detail-section:rotate-180"
    />
  )
  // The body sits flush under the title (the caret is on the right, so there's
  // nothing on the left to clear).
  const body = <div className="pb-3 text-sm text-fbui-gray-700">{children}</div>

  // Non-collapsible: a static header row + always-visible body.
  if (!collapsible) {
    return (
      <section className={cn('group/detail-section', className)}>
        <div className="relative py-2.5">
          <Heading className="m-0 flex items-center gap-2">
            {titleText}
            {spacer}
          </Heading>
          {actions}
        </div>
        {body}
      </section>
    )
  }

  return (
    <Disclosure
      isExpanded={open}
      defaultExpanded={defaultOpen}
      onExpandedChange={onOpenChange}
      className={cn('group/detail-section', className)}
    >
      <div className="relative py-2.5">
        <Heading className="m-0">
          <AriaButton
            slot="trigger"
            className={cn(
              'flex w-full items-center gap-2 rounded text-left outline-none',
              'focus-visible:ring-2 focus-visible:ring-fbui-ring-brand'
            )}
          >
            {titleText}
            {spacer}
            {caret}
          </AriaButton>
        </Heading>
        {actions}
      </div>
      <DisclosurePanel>{body}</DisclosurePanel>
    </Disclosure>
  )
}
DetailSection.displayName = 'DetailSection'
